import React, { useState, useRef, useEffect } from "react";

const F = "'Roboto Mono', monospace";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatHelperProps {
  messages: Message[];
  onSend: (message: string) => void;
  isLoading: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}

export const ChatHelper: React.FC<ChatHelperProps> = ({
  messages,
  onSend,
  isLoading,
  expanded,
  onToggleExpand,
}) => {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSend(input.trim());
    setInput("");
  };

  return (
    <div style={expanded ? styles.containerExpanded : styles.container}>
      {expanded && (
        <div style={styles.header}>
          <span style={styles.headerLabel}>AI ASSISTANT</span>
          <button onClick={onToggleExpand} style={styles.collapseButton}>
            ▼
          </button>
        </div>
      )}

      {expanded && (
        <div style={styles.messages}>
          {messages.map((msg, i) => (
            <div
              key={i}
              style={msg.role === "user" ? styles.userMsg : styles.assistantMsg}
            >
              {msg.content}
            </div>
          ))}
          {isLoading && <div style={styles.loading}>Thinking...</div>}
          <div ref={messagesEndRef} />
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.inputBar}>
        {!expanded && (
          <button
            type="button"
            onClick={onToggleExpand}
            style={styles.expandButton}
          >
            ▲
          </button>
        )}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Claude for help..."
          style={styles.input}
          disabled={isLoading}
        />
        <button type="submit" style={styles.sendButton} disabled={isLoading}>
          NEXT
        </button>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    flexShrink: 0,
    borderTop: "1px solid #E0E0E0",
  },
  containerExpanded: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "40%",
    borderTop: "1px solid #E0E0E0",
    flexShrink: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 12px",
    background: "#1a1a1a",
    borderBottom: "1px solid #E8E8E8",
    flexShrink: 0,
  },
  headerLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "#FFF",
    letterSpacing: 0.5,
  },
  collapseButton: {
    background: "none",
    border: "none",
    fontSize: 8,
    cursor: "pointer",
    color: "#FFF",
    fontFamily: F,
  },
  expandButton: {
    background: "none",
    border: "none",
    fontSize: 8,
    cursor: "pointer",
    color: "#666",
    fontFamily: F,
    padding: "0 8px",
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  userMsg: {
    fontSize: 10,
    padding: "6px 10px",
    background: "#1a1a1a",
    color: "#fff",
    borderRadius: 4,
    alignSelf: "flex-end",
    maxWidth: "80%",
  },
  assistantMsg: {
    fontSize: 10,
    padding: "6px 10px",
    background: "#F8F8F8",
    color: "#1a1a1a",
    borderRadius: 4,
    alignSelf: "flex-start",
    maxWidth: "80%",
    lineHeight: 1.5,
  },
  loading: {
    fontSize: 9,
    color: "#888",
    padding: "4px 0",
  },
  inputBar: {
    display: "flex",
    gap: 6,
    padding: "8px 12px",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: "6px 10px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 10,
    fontFamily: F,
    outline: "none",
  },
  sendButton: {
    padding: "9px 18px",
    background: "#1a1a1a",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
};
